/**
 * Minimal global toast store (FE-02 FR-FE02-15): default 3.5 s, errors 6 s and dismissable,
 * at most 3 visible, one optional action. Rendered by <Toaster />.
 */

export type ToastTone = "info" | "success" | "error";

export interface ToastAction {
  label: string;
  onClick: () => void;
}

export interface ToastItem {
  id: number;
  message: string;
  tone: ToastTone;
  action?: ToastAction;
  /** Dedupe key: a new toast with the same key replaces the old one (e.g. "session-expired"). */
  key?: string;
}

export interface ToastOptions {
  tone?: ToastTone;
  durationMs?: number;
  action?: ToastAction;
  key?: string;
}

const MAX_VISIBLE = 3;
const DEFAULT_MS = 3500;
const ERROR_MS = 6000;

let items: readonly ToastItem[] = [];
let nextId = 1;
const listeners = new Set<() => void>();
const timers = new Map<number, ReturnType<typeof setTimeout>>();

function emit() {
  for (const l of listeners) l();
}

export function dismissToast(id: number): void {
  const timer = timers.get(id);
  if (timer) clearTimeout(timer);
  timers.delete(id);
  const before = items.length;
  items = items.filter((t) => t.id !== id);
  if (items.length !== before) emit();
}

export function showToast(message: string, options: ToastOptions = {}): number {
  const tone = options.tone ?? "info";
  const id = nextId++;
  const kept = options.key ? items.filter((t) => t.key !== options.key) : items;
  for (const t of items) if (!kept.includes(t)) dismissTimer(t.id);
  const item: ToastItem = { id, message, tone, action: options.action, key: options.key };
  const next = [...kept, item];
  for (const t of next.slice(0, Math.max(0, next.length - MAX_VISIBLE))) dismissTimer(t.id);
  items = next.slice(-MAX_VISIBLE);
  const ms = options.durationMs ?? (tone === "error" ? ERROR_MS : DEFAULT_MS);
  timers.set(
    id,
    setTimeout(() => dismissToast(id), ms),
  );
  emit();
  return id;
}

function dismissTimer(id: number) {
  const timer = timers.get(id);
  if (timer) clearTimeout(timer);
  timers.delete(id);
}

export function clearToasts(): void {
  for (const id of timers.keys()) dismissTimer(id);
  items = [];
  emit();
}

export function subscribeToasts(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getToasts(): readonly ToastItem[] {
  return items;
}

/** Convenience API: `toast("Guardado")`, `toast.error("No se pudo")`. */
export const toast = Object.assign(
  (message: string, options?: Omit<ToastOptions, "tone">) => showToast(message, options),
  {
    success: (message: string, options?: Omit<ToastOptions, "tone">) =>
      showToast(message, { ...options, tone: "success" }),
    error: (message: string, options?: Omit<ToastOptions, "tone">) =>
      showToast(message, { ...options, tone: "error" }),
    dismiss: dismissToast,
    clear: clearToasts,
  },
);
